import {
  CRM_API,
  VERWALTUNG_API,
  type CrmAdresse,
  type CrmEinwilligung,
  type CrmKontaktkanal,
  type CrmFiliale,
  type CrmKpi,
  type CrmKundenFilterQuery,
  type CrmKundenListe,
  type CrmPerson,
  type CrmVersionenListe,
  type DeaktivierenRequest,
  type EinladenRequest,
  type EinladenResponse,
  type Freigabe,
  type Me,
  type Mitarbeiter,
  type MitarbeiterListe,
  type ModulesResponse,
  type ModulSetzenRequest,
  type ModulSetzenResponse,
  type VisibleModule,
} from '@portal/contracts';

export class HttpError extends Error {
  /** `code`/`message` aus dem Fehlerformat der Modul-APIs (MODUL-VERTRAG § 3), sofern vorhanden */
  constructor(
    readonly status: number,
    readonly code: string | null = null,
    readonly text: string | null = null,
  ) {
    super(`HTTP ${status}`);
  }
}

async function send<T>(method: string, path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const data = (await res.json().catch(() => null)) as { code?: string; message?: string } | null;
  if (!res.ok) throw new HttpError(res.status, data?.code ?? null, typeof data?.message === 'string' ? data.message : null);
  return data as T;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', headers: { accept: 'application/json' } });
  if (!res.ok) throw new HttpError(res.status);
  return (await res.json()) as T;
}

export const api = {
  me: () => get<Me>('/api/me'),
  modules: () => get<ModulesResponse>('/api/modules'),
  module: (key: string) => get<VisibleModule>(`/api/modules/${encodeURIComponent(key)}`),
  async logout(): Promise<void> {
    const res = await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    const body = res.ok ? ((await res.json()) as { logoutUrl: string | null }) : { logoutUrl: null };
    window.location.assign(body.logoutUrl ?? '/?abgemeldet=1');
  },
  loginUrl: (returnTo: string) => `/auth/login?returnTo=${encodeURIComponent(returnTo)}`,
};

/** Modul „Verwaltung“ (#811) */
export const verwaltungApi = {
  liste: () => get<MitarbeiterListe>(`${VERWALTUNG_API}/mitarbeiter`),
  einladen: (body: EinladenRequest) => send<EinladenResponse>('POST', `${VERWALTUNG_API}/mitarbeiter/einladen`, body),
  modulSetzen: (id: string, body: ModulSetzenRequest) =>
    send<ModulSetzenResponse>('PUT', `${VERWALTUNG_API}/mitarbeiter/${encodeURIComponent(id)}/module`, body),
  deaktivieren: (id: string, body: DeaktivierenRequest) =>
    send<Mitarbeiter>('POST', `${VERWALTUNG_API}/mitarbeiter/${encodeURIComponent(id)}/deaktivieren`, body),
  entscheiden: (id: string, entscheidung: 'bestaetigen' | 'ablehnen') =>
    send<Freigabe>('POST', `${VERWALTUNG_API}/freigaben/${encodeURIComponent(id)}/${entscheidung}`, {}),
};

function query(q: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Modul „CRM“ (#815 Liste; #830 Datensatz, Masken, Versionen — Versionen nur lesen, nirgends löschen) */
export const crmApi = {
  /** #839: Filter = Parameter der URL-Query von /crm (siehe crm-kpi.ts), UND-verknüpft */
  kunden: (seite = 1, groesse = 50, filter: CrmKundenFilterQuery = {}) => get<CrmKundenListe>(`${CRM_API}/kunden${query({ seite, groesse, ...filter })}`),
  kpi: () => get<CrmKpi>(`${CRM_API}/kpi`),
  /** #851 Filialen für die Auswahl der Stammfiliale */
  filialen: () => get<CrmFiliale[]>(`${CRM_API}/filialen`),
  person: (id: string) => get<CrmPerson>(`${CRM_API}/kunden/${encodeURIComponent(id)}`),
  anlegen: (body: Record<string, unknown>) => send<CrmPerson>('POST', `${CRM_API}/kunden`, body),
  aendern: (id: string, body: Record<string, unknown>) => send<CrmPerson>('PATCH', `${CRM_API}/kunden/${encodeURIComponent(id)}`, body),
  deaktivieren: (id: string, body: { kommentar?: string }) => send<CrmPerson>('POST', `${CRM_API}/kunden/${encodeURIComponent(id)}/deaktivieren`, body),
  adresseAnlegen: (id: string, body: Partial<CrmAdresse> & Record<string, unknown>) => send<CrmPerson>('POST', `${CRM_API}/kunden/${encodeURIComponent(id)}/adressen`, body),
  adresseAendern: (id: string, aid: string, body: Record<string, unknown>) =>
    send<CrmPerson>('PATCH', `${CRM_API}/kunden/${encodeURIComponent(id)}/adressen/${encodeURIComponent(aid)}`, body),
  kontaktkanalAnlegen: (id: string, body: Partial<CrmKontaktkanal> & Record<string, unknown>) =>
    send<CrmPerson>('POST', `${CRM_API}/kunden/${encodeURIComponent(id)}/kontaktkanaele`, body),
  kontaktkanalAendern: (id: string, kid: string, body: Record<string, unknown>) =>
    send<CrmPerson>('PATCH', `${CRM_API}/kunden/${encodeURIComponent(id)}/kontaktkanaele/${encodeURIComponent(kid)}`, body),
  einwilligungAnlegen: (id: string, body: Partial<CrmEinwilligung> & Record<string, unknown>) =>
    send<CrmPerson>('POST', `${CRM_API}/kunden/${encodeURIComponent(id)}/einwilligungen`, body),
  einwilligungAendern: (id: string, eid: string, body: Record<string, unknown>) =>
    send<CrmPerson>('PATCH', `${CRM_API}/kunden/${encodeURIComponent(id)}/einwilligungen/${encodeURIComponent(eid)}`, body),
  personVersionen: (id: string, q: { feld?: string; akteur?: string; seite?: number }) =>
    get<CrmVersionenListe>(`${CRM_API}/kunden/${encodeURIComponent(id)}/versionen${query(q)}`),
  versionen: (q: { von?: string; bis?: string; akteur?: string; tabelle?: string; seite?: number; groesse?: number }) => get<CrmVersionenListe>(`${CRM_API}/versionen${query(q)}`),
};
